import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Mail, Phone, Building, Calendar, Tag, FileText, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import BeneficiaryForm from './BeneficiaryForm';

// Define beneficiary schema
const beneficiarySchema = z.object({
  type: z.enum(['Primary', 'Contingent']),
  name: z.string().min(1, 'Name is required'),
  relationship: z.string().min(1, 'Relationship is required'),
  ssn: z.string().optional(),
  dateOfBirth: z.string().optional(),
  percentage: z.string().min(1, 'Percentage is required'),
  address: z.string().optional(),
});

// Form validation schema
const contactSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email format'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  dateOfBirth: z.string().optional(),
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  accountType: z.enum(['Individual', 'Joint', 'Trust', 'Retirement', 'Corporate']).optional(),
  investmentGoals: z.string().optional(),
  riskTolerance: z.enum(['Conservative', 'Moderate', 'Aggressive']).optional(),
  annualIncomeRange: z.string().optional(),
  netWorth: z.string().optional(),
  portfolioValue: z.string().optional(),
  taxFilingStatus: z.enum(['Single', 'Married_Joint', 'Married_Separate', 'Head_of_Household']).optional(),
  lastContactDate: z.string().optional(),
  nextFollowUp: z.string().optional(),
  clientStatus: z.enum(['Active', 'Prospect', 'Inactive', 'Closed']).default('Prospect'),
  beneficiaries: z.array(beneficiarySchema).optional()
    .refine(data => {
      // If there are beneficiaries, at least one must be primary
      if (!data || data.length === 0) return true;
      return data.some(b => b.type === 'Primary');
    }, {
      message: "At least one primary beneficiary is required"
    })
    .refine(data => {
      // If there are primary beneficiaries, their percentages must sum to 100%
      if (!data || data.length === 0) return true;
      const primaryBeneficiaries = data.filter(b => b.type === 'Primary');
      if (primaryBeneficiaries.length === 0) return true;
      
      const sum = primaryBeneficiaries.reduce((acc, b) => acc + (parseFloat(b.percentage) || 0), 0);
      return Math.abs(sum - 100) < 0.01; // Allow for floating point imprecision
    }, {
      message: "Primary beneficiary percentages must total 100%"
    })
    .refine(data => {
      // If there are contingent beneficiaries, their percentages must sum to 100%
      if (!data || data.length === 0) return true;
      const contingentBeneficiaries = data.filter(b => b.type === 'Contingent');
      if (contingentBeneficiaries.length === 0) return true;
      
      const sum = contingentBeneficiaries.reduce((acc, b) => acc + (parseFloat(b.percentage) || 0), 0);
      return Math.abs(sum - 100) < 0.01; // Allow for floating point imprecision
    }, {
      message: "Contingent beneficiary percentages must total 100%"
    }),
  accountRoles: z.string().optional(),
  tags: z.string().optional(),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  
  const methods = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      clientStatus: 'Prospect',
      accountType: 'Individual',
      riskTolerance: 'Moderate',
      taxFilingStatus: 'Single',
      beneficiaries: []
    }
  });

  const { register, handleSubmit, formState: { errors }, reset } = methods;

  const addContact = useMutation({
    mutationFn: async (data: ContactFormData) => {
      // Format data for database
      const formattedData = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        date_of_birth: data.dateOfBirth || null,
        street_address: data.streetAddress || null,
        city: data.city || null,
        state: data.state || null,
        zip: data.zip || null,
        account_type: data.accountType || null,
        investment_goals: data.investmentGoals ? data.investmentGoals.split(',').map(g => g.trim()) : null,
        risk_tolerance: data.riskTolerance || null,
        annual_income_range: data.annualIncomeRange || null,
        net_worth: data.netWorth || null,
        portfolio_value: data.portfolioValue || null,
        tax_filing_status: data.taxFilingStatus || null,
        last_contact_date: data.lastContactDate || null,
        next_follow_up: data.nextFollowUp || null,
        status: data.clientStatus.toLowerCase(),
        beneficiaries: data.beneficiaries || null,
        account_roles: data.accountRoles ? data.accountRoles.split(',').map(r => r.trim()) : null,
        tags: data.tags ? data.tags.split(',').map(t => t.trim()) : null,
        notes: data.notes || null,
        created_at: new Date().toISOString()
      };

      const { data: newContact, error } = await supabase
        .from('clients')
        .insert([formattedData])
        .select('*, client_id')
        .single();

      if (error) throw error;
      return newContact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(`Contact added successfully${data.client_id ? ` with ID: ${data.client_id}` : ''}`);
      reset();
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      toast.error('Failed to add contact');
      console.error('Error adding contact:', error);
    }
  });

  const onSubmit = async (data: ContactFormData) => {
    await addContact.mutate(data);
  };

  return (
    <FormProvider {...methods}>
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Add New Contact</h2>
          {onCancel && (
            <button 
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Identification */}
            <div className="md:col-span-2">
              <h3 className="text-md font-medium text-gray-800 mb-4 pb-2 border-b">Client Identification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register('firstName')}
                      className={`pl-10 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.firstName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('lastName')}
                    className={`block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.lastName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      {...register('dateOfBirth')}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('clientStatus')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="Prospect">Prospect</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="md:col-span-2">
              <h3 className="text-md font-medium text-gray-800 mb-4 pb-2 border-b">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      {...register('email')}
                      className={`pl-10 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      {...register('phone')}
                      placeholder="XXX-XXX-XXXX"
                      className={`pl-10 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.phone ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    {...register('streetAddress')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    {...register('city')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <select
                    {...register('state')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="">Select State</option>
                    {['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'].map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP
                  </label>
                  <input
                    type="text"
                    {...register('zip')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div className="md:col-span-2">
              <h3 className="text-md font-medium text-gray-800 mb-4 pb-2 border-b">Financial Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Type
                  </label>
                  <select
                    {...register('accountType')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="Individual">Individual</option>
                    <option value="Joint">Joint</option>
                    <option value="Trust">Trust</option>
                    <option value="Retirement">Retirement</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Investment Goals
                  </label>
                  <input
                    type="text"
                    {...register('investmentGoals')}
                    placeholder="Retirement,Education,etc."
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Tolerance
                  </label>
                  <select
                    {...register('riskTolerance')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="Conservative">Conservative</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Aggressive">Aggressive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Annual Income Range
                  </label>
                  <input
                    type="text"
                    {...register('annualIncomeRange')}
                    placeholder="$XX,XXX-$XX,XXX"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Net Worth
                  </label>
                  <input
                    type="text"
                    {...register('netWorth')}
                    placeholder="$XXX,XXX"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Portfolio Value
                  </label>
                  <input
                    type="text"
                    {...register('portfolioValue')}
                    placeholder="$XXX,XXX"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Filing Status
                  </label>
                  <select
                    {...register('taxFilingStatus')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="Single">Single</option>
                    <option value="Married_Joint">Married Filing Jointly</option>
                    <option value="Married_Separate">Married Filing Separately</option>
                    <option value="Head_of_Household">Head of Household</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Relationship Data */}
            <div className="md:col-span-2">
              <h3 className="text-md font-medium text-gray-800 mb-4 pb-2 border-b">Relationship Data</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Contact Date
                  </label>
                  <input
                    type="date"
                    {...register('lastContactDate')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Next Follow Up
                  </label>
                  <input
                    type="date"
                    {...register('nextFollowUp')}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Beneficiary Section */}
            <div className="md:col-span-2">
              <BeneficiaryForm name="beneficiaries" />
            </div>

            {/* Additional Information */}
            <div className="md:col-span-2">
              <h3 className="text-md font-medium text-gray-800 mb-4 pb-2 border-b">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Roles
                  </label>
                  <input
                    type="text"
                    {...register('accountRoles')}
                    placeholder="Primary,Secondary,etc."
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Tag className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register('tags')}
                      placeholder="VIP,Referral,etc."
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      {...register('notes')}
                      rows={3}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Additional notes about the client..."
                    ></textarea>
                  </div>
                  {errors.notes && (
                    <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form-level errors */}
          {errors.beneficiaries && errors.beneficiaries.message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Beneficiary Error
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{errors.beneficiaries.message}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={addContact.isPending}
            >
              {addContact.isPending ? (
                <>
                  <span className="loader mr-2"></span>
                  Saving...
                </>
              ) : (
                'Save Contact'
              )}
            </Button>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

export default ContactForm;